import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
} from "react";
import { ArrowRight } from "lucide-react";
import { cn } from "../lib/utils";

/** Grow with the content up to this many lines, then scroll. */
const MAX_LINES = 8;
/** Fallback when getComputedStyle reports a non-numeric line-height. */
const FALLBACK_LINE_HEIGHT = 24;

/**
 * The single chat input used by both the welcome screen and the chat card, so
 * the bar looks identical before and after the morph. It is intentionally
 * background-free: it sits on whichever card is behind it.
 *
 * The forwarded ref points at the underlying textarea so callers can focus it.
 */
const ChatInput = forwardRef(
  (
    {
      value,
      onChange,
      onSend,
      placeholder,
      autoFocus = false,
      disabled = false,
      className,
    },
    ref
  ) => {
    const textareaRef = useRef(null);
    // Holds the latest resize closure for the window listener below, which is
    // subscribed once and so would otherwise capture a stale one.
    const resizeRef = useRef(null);

    useImperativeHandle(ref, () => textareaRef.current, []);

    const resize = useCallback(() => {
      const el = textareaRef.current;
      if (!el) return;

      const styles = window.getComputedStyle(el);
      const lineHeight = parseFloat(styles.lineHeight) || FALLBACK_LINE_HEIGHT;
      const verticalPadding =
        (parseFloat(styles.paddingTop) || 0) + (parseFloat(styles.paddingBottom) || 0);
      const maxHeight = lineHeight * MAX_LINES + verticalPadding;

      // Collapse first so scrollHeight reports the content height, not the
      // height we set on the previous keystroke.
      el.style.height = "auto";

      // A placeholder contributes nothing to scrollHeight, so an empty box
      // with a placeholder long enough to wrap would be sized to one line and
      // clip the rest. Measure it by briefly borrowing the value: this runs in
      // useLayoutEffect, so the swap is reverted before the browser paints,
      // and an empty box has no selection to disturb.
      const measuringPlaceholder = !value && !!placeholder;
      if (measuringPlaceholder) el.value = placeholder;
      const contentHeight = el.scrollHeight;
      if (measuringPlaceholder) el.value = "";

      el.style.height = `${Math.min(contentHeight, maxHeight)}px`;
      el.style.overflowY = contentHeight > maxHeight ? "auto" : "hidden";
    }, [value, placeholder]);

    // Covers typing, pasting a long transcript, the reset after a send, and a
    // placeholder swapped in by a sample-question chip.
    useLayoutEffect(() => {
      resizeRef.current = resize;
      resize();
    }, [resize]);

    // A narrower or wider box re-wraps both the value and the placeholder, so
    // the height has to be recomputed when the viewport changes — otherwise
    // text stays clipped until the next keystroke. Called through a ref so the
    // listener is attached once rather than re-subscribed on every keystroke,
    // and coalesced into a frame so dragging the window edge doesn't force a
    // layout read per event.
    useEffect(() => {
      let frame = null;

      const handleViewportResize = () => {
        if (frame !== null) return;
        frame = window.requestAnimationFrame(() => {
          frame = null;
          resizeRef.current();
        });
      };

      window.addEventListener("resize", handleViewportResize);
      return () => {
        if (frame !== null) window.cancelAnimationFrame(frame);
        window.removeEventListener("resize", handleViewportResize);
      };
    }, []);

    useEffect(() => {
      // preventScroll keeps focus from yanking the page mid layout animation.
      if (autoFocus) textareaRef.current?.focus({ preventScroll: true });
    }, [autoFocus]);

    const handleKeyDown = (event) => {
      if (event.key !== "Enter") return;
      // Let IME composition finish before treating Enter as a send.
      if (event.nativeEvent?.isComposing) return;
      if (event.shiftKey) return; // Shift+Enter inserts a newline.
      event.preventDefault();
      // Typing and pasting stay available while a response is pending; only
      // sending is held back.
      if (disabled) return;
      onSend();
    };

    return (
      <div className={cn("flex w-full items-end gap-3", className)}>
        <textarea
          ref={textareaRef}
          rows={1}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          aria-label="Message CoachAI"
          className="custom-scrollbar flex-1 resize-none overflow-hidden rounded-lg border-0 bg-transparent px-4 py-3 text-base leading-6 text-gray-900 shadow-none placeholder:text-gray-500 focus:border-transparent focus:outline-none focus:ring-0 dark:text-white dark:placeholder:text-gray-400"
        />
        <button
          type="button"
          onClick={() => onSend()}
          disabled={disabled}
          aria-label="Send message"
          className="mb-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gray-200 transition-colors hover:bg-gray-300 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 dark:disabled:hover:bg-gray-700"
        >
          <ArrowRight className="h-5 w-5 text-gray-700 dark:text-white" />
        </button>
      </div>
    );
  }
);

ChatInput.displayName = "ChatInput";

export default ChatInput;
